import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { Instagram, Linkedin, Facebook } from "lucide-react";
import { FaTiktok } from "react-icons/fa";

const PlatformStrategies = () => {
  const anim = useScrollAnimation();

  const platforms = [
    {
      icon: Instagram,
      name: "Instagram",
      color: "from-pink-500 to-purple-600",
      target: "B2C & Lifestyle",
      description: "Reels & Stories für Hotels, Restaurants, Retail und Fashion in Mannheim und der Rhein-Neckar-Region",
      formats: ["Reels (9:16)", "Stories", "Karussell-Posts", "IGTV"],
      idealFor: ["Hotels & Gastronomie", "Retail & E-Commerce", "Fashion & Beauty", "Events"]
    },
    {
      icon: FaTiktok,
      name: "TikTok",
      color: "from-black to-gray-800",
      target: "Gen Z & Millennials",
      description: "Viraler Content für jüngere Zielgruppen mit authentischen, unterhaltsamen Videos",
      formats: ["Kurzvideos (9:16)", "Trends & Challenges", "Behind-the-Scenes", "Educational Content"],
      idealFor: ["Gastronomie", "Retail", "Events", "Bildungseinrichtungen"]
    },
    {
      icon: Linkedin,
      name: "LinkedIn",
      color: "from-blue-600 to-blue-800",
      target: "B2B & Corporate",
      description: "Professioneller Content für Mittelstand und Unternehmen in Mannheim, Stuttgart und Frankfurt",
      formats: ["Thought Leadership", "Company Updates", "Employer Branding", "Case Studies"],
      idealFor: ["B2B-Unternehmen", "Recruiting", "Consulting", "Tech-Firmen"]
    },
    {
      icon: Facebook,
      name: "Facebook",
      color: "from-blue-500 to-blue-700",
      target: "Community Building",
      description: "Community-Aufbau für lokale Events und Veranstaltungen in der Region",
      formats: ["Event-Promotion", "Live-Videos", "Community Posts", "Lokale Reichweite"],
      idealFor: ["Lokale Events", "Community-Projekte", "Veranstaltungsorte", "Vereine"]
    }
  ];

  return (
    <section className="section-padding bg-gradient-to-b from-background to-primary/5">
      <div
        ref={anim.ref}
        className={`container-custom transition-all duration-1000 ${
          anim.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Plattform-Strategien für Unternehmen in der Rhein-Neckar-Region
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            Jede Plattform hat ihre eigene Sprache. Wir entwickeln Content, der perfekt zu Ihrer Zielgruppe passt.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {platforms.map((platform, index) => (
            <div
              key={index}
              className="group bg-background/50 backdrop-blur-xs border border-primary/20 rounded-2xl p-8 hover:border-primary/40 hover:shadow-xl transition-all overflow-hidden relative"
            >
              {/* Gradient background on hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${platform.color} opacity-0 group-hover:opacity-5 transition-opacity`} />
              
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-6">
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${platform.color} flex items-center justify-center text-white shadow-lg`}>
                    <platform.icon className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">{platform.name}</h3>
                    <span className="text-sm text-muted-foreground">{platform.target}</span>
                  </div>
                </div>

                <p className="text-muted-foreground mb-6">
                  {platform.description}
                </p>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2 text-sm uppercase tracking-wide">Content-Formate</h4>
                    <div className="flex flex-wrap gap-2">
                      {platform.formats.map((format, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-muted/50 text-sm rounded-lg"
                        >
                          {format}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2 text-sm uppercase tracking-wide">Ideal für</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {platform.idealFor.map((industry, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                          <span className="text-muted-foreground">{industry}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 bg-primary/5 border border-primary/20 rounded-2xl p-8 text-center">
          <h3 className="text-xl font-semibold mb-4">Multi-Plattform-Strategie</h3>
          <p className="text-muted-foreground max-w-3xl mx-auto">
            Der Erfolg liegt in der richtigen Kombination. Wir entwickeln Cross-Plattform-Strategien, die Ihr Publikum dort erreichen, wo es aktiv ist – von Instagram Reels bis LinkedIn Articles.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PlatformStrategies;
